// Submit a project to The Vibing Library — for humans and for agents.

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/site-footer";
import { BadgeSnippets } from "@/components/library-badge-copy";
import { Check, Copy, Gem } from "@/components/icons";
import { CATEGORY_INTRO } from "@/lib/library-hubs";

const TITLE = "Submit a project — The Vibing Library";
const DESC =
  "Suggest a tool, component, shader or AI skill for The Vibing Library. Humans use the form, agents post JSON to the public API. Every submission is reviewed by hand.";
const URL = "https://vibing.inc/library/submit";

const AGENT_PROMPT = `You can submit a resource to The Vibing Library (vibing.inc) on my behalf.

POST https://vibing.inc/api/public/library/submit
Content-Type: application/json

{
  "name": "Project name",
  "url": "https://example.com",
  "description": "One sentence on what it does.",
  "whyGem": "Why it is worth knowing.",
  "category": "UI Components & Libraries",
  "subcategory": "React",
  "tags": ["react", "animation"],
  "author": "Maker or org",
  "license": "MIT",
  "contactEmail": "you@example.com",
  "submittedBy": "agent"
}

The response returns { id, status, duplicate }. Status "pending" means it is
queued for human review; "duplicate" means the link already exists — check
duplicate.matches before resubmitting. Browse or search first with
GET https://vibing.inc/api/public/library/search?q=<query>.`;

export const Route = createFileRoute("/library/submit")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: SubmitPage,
});

type Sent = {
  id: string;
  status: string;
  name: string;
  message: string;
  duplicate: { verdict: string; matches: { slug: string; name: string; pageUrl: string }[] };
};

const field =
  "mt-1.5 w-full rounded-xl bg-card px-3 py-2.5 text-sm ring-1 ring-border/60 transition-shadow placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor: string }) {
  return (
    <label htmlFor={htmlFor} className="text-sm text-foreground">
      {children}
    </label>
  );
}

function SubmitPage() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<Sent | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const form = new FormData(e.currentTarget);
    const value = (k: string) => String(form.get(k) ?? "").trim();
    const payload = {
      name: value("name"),
      url: value("url"),
      description: value("description"),
      whyGem: value("whyGem"),
      category: value("category"),
      subcategory: value("subcategory"),
      tags: value("tags")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 12),
      author: value("author"),
      license: value("license"),
      imageUrl: value("imageUrl"),
      contactEmail: value("contactEmail"),
      company: value("company"),
      submittedBy: "human" as const,
    };

    try {
      const res = await fetch("/api/public/library/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = (await res.json()) as Sent & { message?: string; issues?: { message: string }[] };
      if (!res.ok) {
        setError(body.issues?.[0]?.message ?? body.message ?? "Something went wrong.");
      } else {
        setSent(body);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch {
      setError("Network error — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-svh bg-background">
      <main className="mx-auto max-w-[1000px] px-6 md:px-10">
        <div className="pt-[clamp(3rem,6vh,5rem)] pb-10">
          <Link
            to="/library"
            className="inline-flex items-center gap-2 rounded-full py-1.5 px-3 text-sm text-muted-foreground ring-1 ring-border/60 transition-colors hover:text-foreground hover:bg-muted/40"
          >
            Back to the library
          </Link>

          <h1
            className="mt-7 flex items-center gap-3 font-display leading-[1.02] tracking-[-0.035em]"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)" }}
          >
            <Gem aria-hidden style={{ width: "0.72em", height: "0.72em" }} />
            Submit a project
          </h1>
          <p className="mt-5 max-w-[58ch] text-lg leading-snug text-muted-foreground text-pretty">
            Built something worth knowing, or found a gem we missed? Send it over.
            It is free, there is no account, and every submission is opened and
            read by a human before it lands on the shelf — usually within a few days.
          </p>
        </div>

        {sent ? (
          <section className="rounded-3xl bg-card p-6 ring-1 ring-border/60 md:p-8">
            <h2 className="font-display text-2xl tracking-tight">Thanks — it's in the queue</h2>
            <p className="mt-3 max-w-[60ch] text-sm text-muted-foreground">{sent.message}</p>
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              Reference {sent.id.slice(0, 8)} · {sent.name}
            </p>
            {sent.duplicate.matches.length > 0 ? (
              <div className="mt-5 rounded-2xl bg-muted/40 p-4">
                <p className="text-sm">Already on the shelf, or close to it:</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {sent.duplicate.matches.map((m) => (
                    <li key={m.slug}>
                      <a className="underline underline-offset-4" href={m.pageUrl}>
                        {m.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/library"
                className="inline-flex min-h-[40px] items-center rounded-full bg-foreground px-4 text-sm text-background"
              >
                Back to the library
              </Link>
              <button
                type="button"
                onClick={() => setSent(null)}
                className="inline-flex min-h-[40px] items-center rounded-full px-4 text-sm ring-1 ring-border/60 hover:bg-muted/60"
              >
                Submit another
              </button>
            </div>
          </section>
        ) : (
          <form onSubmit={onSubmit} className="rounded-3xl bg-card p-6 ring-1 ring-border/60 md:p-8">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-1">
                <Label htmlFor="name">Project name *</Label>
                <input id="name" name="name" required maxLength={120} className={field} placeholder="Mesher" />
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="url">Link *</Label>
                <input
                  id="url"
                  name="url"
                  required
                  type="url"
                  maxLength={500}
                  className={field}
                  placeholder="https://…"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="description">One-line description</Label>
                <input
                  id="description"
                  name="description"
                  maxLength={400}
                  className={field}
                  placeholder="What it does, in one sentence."
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="whyGem">Why it's a gem</Label>
                <textarea
                  id="whyGem"
                  name="whyGem"
                  rows={3}
                  maxLength={400}
                  className={field}
                  placeholder="What makes it better than the obvious alternative?"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <select id="category" name="category" className={field} defaultValue="">
                  <option value="">Not sure — you pick</option>
                  {Object.keys(CATEGORY_INTRO).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="subcategory">Subcategory</Label>
                <input id="subcategory" name="subcategory" maxLength={80} className={field} placeholder="React" />
              </div>
              <div>
                <Label htmlFor="tags">Tags</Label>
                <input id="tags" name="tags" maxLength={200} className={field} placeholder="react, animation, free" />
              </div>
              <div>
                <Label htmlFor="author">Maker</Label>
                <input id="author" name="author" maxLength={120} className={field} placeholder="Person or org" />
              </div>
              <div>
                <Label htmlFor="license">Licence</Label>
                <input id="license" name="license" maxLength={60} className={field} placeholder="MIT, free, paid…" />
              </div>
              <div>
                <Label htmlFor="imageUrl">Preview image URL</Label>
                <input id="imageUrl" name="imageUrl" maxLength={500} className={field} placeholder="https://…" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="contactEmail">Your email (optional)</Label>
                <input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  maxLength={255}
                  className={field}
                  placeholder="So we can tell you when it goes live."
                />
              </div>
            </div>

            {/* Honeypot — hidden from people, irresistible to bots. */}
            <div aria-hidden className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="company">Company</label>
              <input id="company" name="company" tabIndex={-1} autoComplete="off" />
            </div>

            {error ? (
              <p role="alert" className="mt-5 text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={busy}
                className="inline-flex min-h-[44px] items-center rounded-full bg-foreground px-5 text-sm text-background transition-opacity disabled:opacity-60"
              >
                {busy ? "Sending…" : "Submit for review"}
              </button>
              <p className="text-xs text-muted-foreground">
                No account, no newsletter. We only email you if you asked us to.
              </p>
            </div>
          </form>
        )}

        <section aria-labelledby="agents-heading" className="mt-14 border-t border-border/60 pt-10">
          <h2 id="agents-heading" className="font-display text-2xl tracking-tight">
            Submitting as an agent
          </h2>
          <p className="mt-3 max-w-[62ch] text-sm text-muted-foreground">
            The library has a machine surface. Agents can search it, read it and now
            add to it — one JSON POST, no key required. Rate limited and always
            queued for human review.
          </p>
          <div className="mt-5 rounded-2xl bg-card p-4 ring-1 ring-border/60">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-muted-foreground">
                POST /api/public/library/submit
              </span>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(AGENT_PROMPT);
                  setCopiedPrompt(true);
                  setTimeout(() => setCopiedPrompt(false), 1800);
                }}
                className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground ring-1 ring-border/60 hover:bg-muted/60 hover:text-foreground"
              >
                {copiedPrompt ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
                {copiedPrompt ? "Copied" : "Copy agent prompt"}
              </button>
            </div>
            <pre className="mt-3 overflow-x-auto rounded-xl bg-muted/50 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
              {AGENT_PROMPT}
            </pre>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Full endpoint list and the MCP server live on{" "}
            <Link to="/for-agents" className="underline underline-offset-4">
              /for-agents
            </Link>
            .
          </p>
        </section>

        <section aria-labelledby="badge-heading" className="mt-14 border-t border-border/60 pt-10 pb-16">
          <h2 id="badge-heading" className="font-display text-2xl tracking-tight">
            Already featured?
          </h2>
          <p className="mt-3 max-w-[62ch] text-sm text-muted-foreground">
            Show it off. Grab the badge and paste it into your README or site —
            it links straight back to your entry.
          </p>
          <div className="mt-6">
            <BadgeSnippets slug="your-project-slug" />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Replace <code className="font-mono">your-project-slug</code> with your entry's
            slug, or copy the ready-made snippet from{" "}
            <Link to="/library/badge" className="underline underline-offset-4">
              the badge page
            </Link>
            .
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
